import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Center, PasswordInput, Stack, Text, TextInput } from '@mantine/core';

import { Wordmark } from '../components/Wordmark';
import { AUTH_MODE, login } from '../lib/auth';

// Signing in, in the two shapes this app has. The product build asks for an
// address and a password an admin gave out; there is no registration and no
// reset link (concept section 5). The header build, for local work and
// tests, takes the address alone, which is exactly as unsafe as it sounds
// and is why it is a build-time setting.
export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const header = AUTH_MODE === 'header';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack>
          {header ? (
            <Text size="sm" c="dimmed">
              This build signs in by address alone. Try <code>jo@assetshelf.test</code> (admin) or{' '}
              <code>anna@assetshelf.test</code>.
            </Text>
          ) : null}
          <TextInput
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          {header ? null : (
            <PasswordInput
              label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          )}
          {error ? (
            <Alert color="red" variant="light">
              {error.message}
            </Alert>
          ) : null}
          <Button type="submit" loading={busy} disabled={!email.trim()}>
            Sign in
          </Button>
          {header ? null : (
            <Text size="xs" c="dimmed">
              No account? An admin of this shelf creates one for you.
            </Text>
          )}
        </Stack>
      </form>
    </Frame>
  );
}

/** A signed-in account this shelf does not know. */
function Refused({ message, onSignOut }: { message: string; onSignOut: () => void }) {
  return (
    <Frame>
      <Stack>
        <Alert color="yellow" variant="light">
          {message}
        </Alert>
        <Button variant="default" onClick={onSignOut}>
          Sign out
        </Button>
      </Stack>
    </Frame>
  );
}

SignInPage.Refused = Refused;

function Frame({ children }: { children: ReactNode }) {
  return (
    <Center mih="100dvh" p="md">
      <Card withBorder padding="lg" radius="md" maw={400} w="100%">
        <Stack>
          <div>
            <Wordmark />
            <Text size="sm" c="dimmed" mt={4}>
              The folders shared with you, straight from the bucket.
            </Text>
          </div>
          {children}
        </Stack>
      </Card>
    </Center>
  );
}
